import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { useSnackbar } from "../../../../../contexts/SnackbarContext";
import { useErrorSnackbar } from "../../../../../hooks/useErrorSnackbar";
import { backendService } from "../../../../../services/backend.service";
import { errorText, uiVisibleErrorKey } from "../../../../../utils/errors";
import { isSchedulePushConfig } from "./schedulePushConfigShape";
import { schedulePushKey } from "./schedulePushConsts";
import type { SaveSchedulePushConfigBody, SchedulePushConfig, SchedulePushSource, SchedulePushTarget } from "./schedulePushTypes";
import { useSchedulePushTranslation } from "./useSchedulePushTranslation";

export const SCHEDULE_PUSH_QUERY_KEY = "schedule-push";
/** The facility's units change when someone edits HostedTime, which is rare; the list is re-read when the panel remounts. */
const VENDOR_UNITS_STALE_MS = 5 * 60_000;

/** Every vendor's config for one department — what the department form's own Save invalidates. */
export const schedulePushDepartmentConfigKey = (departmentId: string) => [SCHEDULE_PUSH_QUERY_KEY, "config", departmentId];

export const schedulePushConfigQueryKey = (departmentId: string, source: SchedulePushSource) => [...schedulePushDepartmentConfigKey(departmentId), source];

/**
 * The panel's one read. `retry` is off: every failure here (403, a department without the integration) is an
 * answer, and the panel shows it rather than spinning through three attempts first. An answer that isn't a config
 * (an empty body, an HTML page from a proxy) fails the same way, so the section shows its load error and Retry
 * instead of reading fields that aren't there.
 */
export function useSchedulePushConfig(source: SchedulePushSource, enabled: boolean) {
	const { chosenDepartment } = useChosenDepartment();
	const { trackError } = useAnalytics();
	return useQuery<SchedulePushConfig>({
		queryKey: schedulePushConfigQueryKey(chosenDepartment.department_id, source),
		queryFn: async () => {
			try {
				const { data } = await backendService.getSchedulePushConfig(chosenDepartment.department_id, source);
				if (!isSchedulePushConfig(data)) throw new Error(`The ${source} schedule-push config answer isn't a config`);
				return data;
			} catch (error) {
				// Tracked here as well as shown: for a department without the integration the panel stays hidden, so a
				// failed read would otherwise reach nobody.
				trackError("schedule_push_config_load_error", { department_id: chosenDepartment.department_id, source, error_message: errorText(error) });
				throw error;
			}
		},
		enabled,
		retry: false,
	});
}

/**
 * The facility's units, read live from the vendor. Keyed by the facility code so typing a new code (before the first
 * send) loads that facility's list. Failures are answers (`schedule.push.units.unreachable`, a facility the login
 * can't see), so no retry: the panel shows the reason and a Retry button.
 */
export function useSchedulePushVendorUnits(source: SchedulePushSource, facilityCode: string, enabled: boolean) {
	const { chosenDepartment } = useChosenDepartment();
	const { trackError } = useAnalytics();
	return useQuery({
		queryKey: [SCHEDULE_PUSH_QUERY_KEY, "vendor-units", chosenDepartment.department_id, source, facilityCode],
		queryFn: async () => {
			try {
				return (await backendService.getSchedulePushVendorUnits(chosenDepartment.department_id, source, facilityCode)).data;
			} catch (error) {
				// Shown in the panel too; tracked so a facility HostedTime keeps refusing to list is visible across visits.
				trackError("schedule_push_vendor_units_error", {
					department_id: chosenDepartment.department_id,
					source,
					error_key: uiVisibleErrorKey(error),
					error_message: errorText(error),
				});
				throw error;
			}
		},
		enabled: enabled && facilityCode.trim().length > 0,
		retry: false,
		staleTime: VENDOR_UNITS_STALE_MS,
	});
}

/**
 * Save the facility code and the whole map. The response is the re-classified config, written straight into the
 * cache so every row's status reflects the map that was just stored — not the one the user was editing.
 */
export function useSaveSchedulePushConfig(source: SchedulePushSource) {
	const { t } = useSchedulePushTranslation(source);
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const showErrorSnackbar = useErrorSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation<SchedulePushConfig, Error, SaveSchedulePushConfigBody>({
		mutationFn: async (body) => {
			track("schedule_push_save_start", { department_id: departmentId, source, map_rows: body.mapping.length });
			return (await backendService.saveSchedulePushConfig(departmentId, source, body)).data;
		},
		retry: false,
		onSuccess: (config, body) => {
			queryClient.setQueryData(schedulePushConfigQueryKey(departmentId, source), config);
			track("schedule_push_save_success", { department_id: departmentId, source, map_rows: body.mapping.length });
			showSnackbar(t(schedulePushKey("save.success")), "success");
		},
		onError: (error) => {
			trackError("schedule_push_save_error", {
				department_id: departmentId,
				source,
				error_key: uiVisibleErrorKey(error),
				error_message: errorText(error),
			});
			showErrorSnackbar(t(schedulePushKey("save.failed")), { action: "schedule_push_save", department_id: departmentId }, error);
			// A refusal is usually news about the server's state (sending switched on elsewhere, shifts now sent, the
			// login removed): re-read it so the locks and reasons on screen match the one that refused. The edit in
			// progress survives — the panel resets its draft only when the STORED map changes.
			void queryClient.invalidateQueries({ queryKey: schedulePushConfigQueryKey(departmentId, source) });
		},
	});
}

/**
 * Start or stop sending. Takes effect at once, with no preview required. The config is re-read after it lands because
 * sending also decides whether the facility code can be edited.
 */
export function useSetSchedulePushActive(source: SchedulePushSource) {
	const { t } = useSchedulePushTranslation(source);
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const showErrorSnackbar = useErrorSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation<SchedulePushTarget, Error, boolean>({
		mutationFn: async (active) => {
			track("schedule_push_toggle_start", { department_id: departmentId, source, active });
			return (await backendService.setSchedulePushActive(departmentId, source, active)).data;
		},
		retry: false,
		onSuccess: (target) => {
			queryClient.setQueryData<SchedulePushConfig>(schedulePushConfigQueryKey(departmentId, source), (previous) => previous && { ...previous, target });
			void queryClient.invalidateQueries({ queryKey: schedulePushConfigQueryKey(departmentId, source) });
			track("schedule_push_toggle_success", { department_id: departmentId, source, active: target.active });
			showSnackbar(t(target.active ? schedulePushKey("toggle.turnedOn") : schedulePushKey("toggle.turnedOff")), "success");
		},
		onError: (error, active) => {
			trackError("schedule_push_toggle_error", {
				department_id: departmentId,
				source,
				active,
				error_key: uiVisibleErrorKey(error),
				error_message: errorText(error),
			});
			showErrorSnackbar(t(schedulePushKey("toggle.failed")), { action: "schedule_push_toggle", department_id: departmentId }, error);
			// Same as a refused save: a 409 here means the integration or login changed, so the reasons must refresh.
			void queryClient.invalidateQueries({ queryKey: schedulePushConfigQueryKey(departmentId, source) });
		},
	});
}
